import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tent, MapPin, Flag, CheckCircle2, Award, Layers, PenLine, Move, Lock } from "lucide-react";
import FlashCardActivity from "@/components/activities/FlashCardActivity";
import QuickWriteActivity from "@/components/activities/QuickWriteActivity";
import DragDropMatchActivity from "@/components/activities/DragDropMatchActivity";
import QuizFlow from "@/components/QuizFlow";
import LockedOverlay from "@/components/LockedOverlay";
import LockedTrailScreen from "@/components/LockedTrailScreen";
import { usePlanState } from "@/hooks/usePlanState";

const ACTIVITY_META = {
  flashcards: { icon: Layers, label: "Flash Card Flip", ap: 10 },
  quick_write: { icon: PenLine, label: "Quick Write", ap: 15 },
  drag_match: { icon: Move, label: "Drag & Match", ap: 20 },
};

const STAGE_META = {
  base_camp: { label: "Base Camp", icon: Tent },
  trail_1: { label: "Trail 1", icon: MapPin },
  trail_2: { label: "Trail 2", icon: MapPin },
  trail_3: { label: "Trail 3", icon: MapPin },
  summit: { label: "Summit", icon: Flag },
};

// Stage card icons + labels

const ExpeditionView = () => {
  const { id } = useParams();
  const [exp, setExp] = useState(null);
  const [activeStage, setActiveStage] = useState(null);
  const [quizMode, setQuizMode] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [quizSeed, setQuizSeed] = useState(0);
  const [activityBusy, setActivityBusy] = useState(null);
  const { state: planState, usageForClimber } = usePlanState();

  // Compute whether this expedition's TRAIL_2+ stages are locked for the user.
  // Free users get one full expedition per climber; subsequent expeditions are
  // capped at trail_1, UNLESS this expedition was grandfathered (in progress
  // when their trial expired) AND they are still within the grace window.
  const isFreeAndCapped = (() => {
    if (!planState || !exp) return false;
    if (planState.is_premium) return false;
    if (exp.grandfathered_pre_free && planState?.grace?.in_grace) return false;
    const usage = usageForClimber(exp.student_id);
    // The 'free full expedition' is used if this climber has ANY completed
    // expedition OTHER than the current one.
    const otherCompleted = (usage?.completed_expeditions || 0) - (exp.status === "completed" ? 1 : 0);
    return otherCompleted >= 1;
  })();

  const isStageLockedForFree = (stageName) => {
    if (!isFreeAndCapped) return false;
    return !["base_camp", "trail_1"].includes(stageName);
  };

  // Reshuffle whenever the active stage changes OR the quiz is reopened. Returns
  // both the shuffled questions and a mapping shuffled-index → original-index so
  // the backend grader (which keys by original index) gets the right answers.
  const { shuffledQuiz, originalIndexOf } = useMemo(() => {
    if (!activeStage?.quiz) return { shuffledQuiz: [], originalIndexOf: () => 0 };
    const pairs = activeStage.quiz.map((q, i) => ({ q, orig: i }));
    // Fisher-Yates on the (question, originalIndex) pairs
    for (let i = pairs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
    }
    const shuffled = pairs.map(({ q }) => {
      const opts = Array.isArray(q.options) ? q.options.slice() : [];
      const correctValue = opts[q.answer];
      for (let i = opts.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [opts[i], opts[j]] = [opts[j], opts[i]];
      }
      const newAnswer = opts.indexOf(correctValue);
      return { ...q, options: opts, answer: newAnswer >= 0 ? newAnswer : 0 };
    });
    const origMap = pairs.map((p) => p.orig);
    return { shuffledQuiz: shuffled, originalIndexOf: (qi) => origMap[qi] ?? qi };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStage?.name, quizSeed]);

  const load = async () => {
    const r = await api.get(`/expeditions/${id}`);
    setExp(r.data);
    if (!activeStage && r.data.stages.length) {
      const idx = Math.min(r.data.current_stage_index, r.data.stages.length - 1);
      setActiveStage(r.data.stages[idx]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Called by QuizFlow after the summary "Continue" is pressed — reloads
  // expedition, closes quiz mode, and advances to the next incomplete stage.
  const onQuizComplete = async (serverResult) => {
    if (serverResult?.next_path === "explorer" || serverResult?.next_path === "rescue") {
      setRecommendation({ path: serverResult.next_path, score: Math.round(serverResult.score_percent || 0) });
    }
    setQuizMode(false);
    await load();
    // Find next incomplete stage (after refresh, completed list will include this stage).
    const refreshed = await api.get(`/expeditions/${id}`);
    const next = refreshed.data.stages.find((s) => !refreshed.data.completed_stages.includes(s.name));
    if (next) setActiveStage(next);
  };

  if (!exp) {
    return (
      <div className="min-h-screen bg-mm-parchment bg-topo">
        <Navbar />
        <div className="container mx-auto py-20 text-center text-mm-navy/60">Charting trail...</div>
      </div>
    );
  }

  const completed = exp.completed_stages;
  const totalStages = exp.stages.length;
  const progressPct = Math.round((completed.length / totalStages) * 100);

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />
      <div className="container mx-auto py-10">
        <Link to="/dashboard" className="text-sm text-mm-steel font-bold hover:text-mm-navy" data-testid="back-to-dashboard">← Back to HQ</Link>

        <div className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">{exp.difficulty === "rescue" ? "Rescue Route" : exp.difficulty === "explorer" ? "Explorer's Challenge" : "Standard Trail"}</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black text-mm-navy mt-2" data-testid="expedition-title">{exp.title}</h1>
            <p className="text-mm-navy/70 mt-2 max-w-2xl">{exp.summary}</p>
          </div>
          <div className="bg-white border border-mm-navy/10 rounded-2xl px-5 py-3 min-w-[220px]">
            <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Elevation gained</div>
            <div className="font-heading text-2xl font-black text-mm-navy">{progressPct}%</div>
            <div className="elevation-bar mt-2"><div className="elevation-fill" style={{ width: `${progressPct}%` }} /></div>
          </div>
        </div>

        {recommendation && (
          <div className="mt-6 bg-mm-gold/15 border-2 border-mm-gold rounded-2xl p-5" data-testid="route-recommendation">
            <div className="font-heading font-bold text-mm-navy">
              {recommendation.path === "rescue"
                ? "We suggest a Rescue Route — extra scaffolding awaits."
                : "You're soaring — try an Explorer's Challenge next!"}
            </div>
            <div className="text-sm text-mm-navy/70 mt-1">Based on your Base Camp score of {recommendation.score}%.</div>
            <Link to={`/expeditions/new?studentId=${exp.student_id}`} className="btn-trail btn-gold mt-3 text-sm">Plot the next climb</Link>
          </div>
        )}

        {/* Stage list */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-3" data-testid="stage-list">
            {exp.stages.map((s, idx) => {
              const meta = STAGE_META[s.name] || { label: s.name, icon: MapPin };
              const Icon = meta.icon;
              const done = completed.includes(s.name);
              const isActive = activeStage?.name === s.name;
              const stageLocked = isStageLockedForFree(s.name);
              return (
                <button key={s.name} onClick={() => { setActiveStage(s); setQuizMode(false); }}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-start gap-3 ${
                    isActive ? "border-mm-gold bg-mm-gold/10" : done ? "border-mm-steel bg-mm-steel/5" : stageLocked ? "border-mm-gold/30 bg-mm-gold/5" : "border-mm-navy/15 bg-white hover:border-mm-steel"
                  }`}
                  data-testid={`stage-tab-${s.name}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${done ? "bg-mm-steel text-white" : stageLocked ? "bg-mm-gold/30 text-mm-navy" : "bg-mm-steel/15 text-mm-navy"}`}>
                    {done ? <CheckCircle2 size={18} strokeWidth={2.5} /> :
                     stageLocked ? <Lock size={16} strokeWidth={2.5} data-testid={`stage-tab-lock-${s.name}`} /> :
                     <Icon size={18} strokeWidth={2.5} />}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-mm-steel">Stage {idx + 1}{stageLocked ? " · Summit Pass" : ""}</div>
                    <div className="font-heading font-bold text-mm-navy">{s.title}</div>
                    {done && <div className="text-xs text-mm-steel font-bold mt-0.5">Score: {Math.round(exp.stage_scores[s.name] || 0)}%</div>}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="lg:col-span-2 stage-card p-8" data-testid="stage-detail">
            {activeStage && !quizMode && isStageLockedForFree(activeStage.name) && (
              <LockedTrailScreen stageName={activeStage.name} />
            )}
            {activeStage && !quizMode && !isStageLockedForFree(activeStage.name) && (
              <>
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">{STAGE_META[activeStage.name]?.label}</div>
                <h2 className="font-heading text-2xl sm:text-3xl font-black text-mm-navy mt-1">{activeStage.title}</h2>

                <div className="mt-5" data-testid="stage-objectives">
                  <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Summary — you'll learn to</div>
                  <ul className="mt-2 space-y-1.5 text-mm-navy/85 list-disc list-inside">
                    {activeStage.objectives?.map((o, i) => <li key={i}>{o}</li>)}
                  </ul>
                </div>

                <div className="mt-6" data-testid="stage-reading">
                  <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Reading</div>
                  <div className="mt-2 text-mm-navy/85 leading-relaxed whitespace-pre-line">{activeStage.explanation || activeStage.content}</div>
                </div>

                <div className="mt-6">
                  <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Activities</div>
                  <div className="mt-3 space-y-4" data-testid="activities-list">
                    {(activeStage.activities || []).map((a, i) => {
                      const compKey = `${activeStage.name}:${i}`;
                      // quick_write has been retired — hide it in older expeditions.
                      if (a && a.type === "quick_write") return null;
                      // Backward compat: old expeditions have strings
                      if (typeof a === "string") {
                        return <div key={compKey} className="text-mm-navy/85 px-4 py-2 rounded-lg bg-mm-parchment border border-mm-navy/10">• {a}</div>;
                      }
                      const meta = ACTIVITY_META[a.type] || ACTIVITY_META.flashcards;
                      const Icon = meta.icon;
                      const completion = exp.activity_completions?.[compKey];
                      const isDone = !!completion;
                      const busy = activityBusy === compKey;
                      // Free-tier activity lock: matching is now free for everyone.
                      // Only legacy quick_write activities remain gated (retired feature).
                      const isLockedForFree = !!planState && !planState.is_premium
                        && a.type === "quick_write";

                      const onCompleteActivity = async (responseText) => {
                        setActivityBusy(compKey);
                        try {
                          const r = await api.post(`/expeditions/${id}/complete-activity`, {
                            stage_name: activeStage.name,
                            activity_index: i,
                            response: responseText,
                          });
                          if (r.data?.status === "already_completed") {
                            toast.info("Already completed");
                          } else {
                            toast.success(`Trail marker planted! +${r.data.ap_earned} AP`);
                          }
                          await load();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Could not complete activity");
                        } finally {
                          setActivityBusy(null);
                        }
                      };

                      return (
                        isLockedForFree ? (
                          <div
                            key={compKey}
                            className="relative rounded-2xl border border-mm-navy/10 bg-white overflow-hidden min-h-[160px]"
                            data-testid={`activity-card-${i}`}
                          >
                            <div className="px-5 py-4 flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-mm-gold/30 text-mm-navy">
                                  <Lock size={18} strokeWidth={2.5} data-testid={`activity-locked-icon-${i}`} />
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-widest font-bold text-mm-steel">{meta.label} · +{meta.ap} AP</div>
                                  <div className="font-heading font-bold text-mm-navy">{a.title || meta.label}</div>
                                </div>
                              </div>
                              <span className="text-xs font-bold text-mm-navy uppercase tracking-widest" data-testid={`activity-locked-badge-${i}`}>Summit Pass</span>
                            </div>
                            <LockedOverlay
                              testid={`activity-locked-overlay-${i}`}
                              message="This activity is part of the Summit Pass. Upgrade for just $5/month to unlock all activity types and keep climbing! 🧭"
                              compact
                            />
                          </div>
                        ) : (
                          <details key={compKey} className="rounded-2xl border border-mm-navy/10 bg-white overflow-hidden" open={!isDone} data-testid={`activity-card-${i}`}>
                            <summary className="px-5 py-4 cursor-pointer flex items-center justify-between gap-3 hover:bg-mm-parchment/50">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDone ? "bg-mm-steel text-white" : "bg-mm-steel/15 text-mm-navy"}`}>
                                  {isDone ? <CheckCircle2 size={18} strokeWidth={2.5} data-testid={`activity-done-${i}`} /> : <Icon size={18} strokeWidth={2.5} />}
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-widest font-bold text-mm-steel">{meta.label} · +{meta.ap} AP</div>
                                  <div className="font-heading font-bold text-mm-navy">{a.title || meta.label}</div>
                                </div>
                              </div>
                              {isDone && <span className="text-xs font-bold text-mm-steel uppercase tracking-widest">Complete</span>}
                            </summary>
                            <div className="px-5 pb-5 pt-3 border-t border-mm-navy/10">
                              {a.type === "flashcards" && (
                                <FlashCardActivity activity={a} completed={isDone} onComplete={() => onCompleteActivity()} busy={busy} />
                              )}
                              {a.type === "quick_write" && (
                                <QuickWriteActivity activity={a} completed={isDone} completion={completion} onComplete={onCompleteActivity} busy={busy} />
                              )}
                              {a.type === "drag_match" && (
                                <DragDropMatchActivity activity={a} completed={isDone} onComplete={() => onCompleteActivity()} busy={busy} />
                              )}
                            </div>
                          </details>
                        )
                      );
                    })}
                  </div>
                </div>

                <div className="mt-8">
                  {completed.includes(activeStage.name) ? (
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-mm-steel/15 text-mm-navy rounded-full font-bold">
                      <Award size={16} strokeWidth={2.5} /> Stage complete — {Math.round(exp.stage_scores[activeStage.name] || 0)}%
                    </div>
                  ) : (
                    <button onClick={() => { setQuizSeed((s) => s + 1); setQuizMode(true); }} className="btn-trail btn-gold" data-testid="start-quiz-btn">
                      Take the trail check
                    </button>
                  )}
                </div>
              </>
            )}

            {activeStage && quizMode && (
              <QuizFlow
                expeditionId={id}
                stage={{ ...activeStage, quiz: shuffledQuiz }}
                originalIndex={originalIndexOf}
                onExit={() => setQuizMode(false)}
                onComplete={onQuizComplete}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpeditionView;
